import { Item, ItemContent, ItemTitle } from '@heidi/design-system/item';
import { Link } from '@heidi/design-system/link';
import { focusRing } from '@heidi/design-system/styles';
import { typographyVariant } from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import type { ReactNode } from 'react';

/**
 * One run row, shared by the overview's "Previous runs" rail and the run
 * chat's "Recent runs" card. A run IS a chat, so clicking a row navigates to
 * that run's chat rather than opening a transcript in place; ``selected`` marks
 * the run already open.
 *
 * The two surfaces label a run differently — the overview needs the run's own
 * title (it sits under the routine's configuration, away from any run) plus a
 * compact age, while the run chat is already inside the routine and only needs
 * "when" — so the label, trailing ``meta`` and optional ``leading`` node are
 * all per-caller.
 *
 * ``appearance="card"`` matches the V2 session-panel row geometry
 * ({@link Item} ``size="sm"``) used inside the run chat's recent-runs card.
 */
export function RoutineRunRow({
  appearance = 'overview',
  label,
  meta,
  leading,
  selected,
  disabled = false,
  href,
  onClick,
}: {
  /** ``card`` — scribe-aligned Item row; ``overview`` — Previous-runs rail. */
  appearance?: 'overview' | 'card';
  label: string;
  /** Optional trailing detail, e.g. the compact run age. */
  meta?: string;
  /**
   * Overview appearance only: a caller-supplied leading node (e.g. the
   * sidebar's status glyph) rendered ahead of the label at the same
   * x-position.
   */
  leading?: ReactNode;
  /** The run currently open on the chat page. */
  selected: boolean;
  /** Prevent repeat activation while an action is in flight. */
  disabled?: boolean;
  /** Optional external destination rendered with the same row geometry. */
  href?: string;
  onClick?: () => void;
}): JSX.Element {
  if (appearance === 'card') {
    return (
      <Item asChild size="sm">
        <button
          type="button"
          data-testid="routine-run-row"
          onClick={onClick}
          disabled={disabled}
          aria-current={selected ? 'true' : undefined}
          className={cn('text-text-secondary', focusRing, selected && 'bg-surface-hover')}
        >
          <ItemContent>
            <ItemTitle className="truncate">{label}</ItemTitle>
          </ItemContent>
        </button>
      </Item>
    );
  }

  const className = cn(
    'flex h-10 items-center gap-4 rounded-md px-3 text-start outline-none transition-colors',
    selected ? 'hover:bg-background-secondary' : 'hover:bg-surface-hover',
    'focus-visible:ring-2 focus-visible:ring-border-focus',
    disabled && 'disabled:cursor-default disabled:hover:bg-transparent',
    selected && 'bg-background-secondary'
  );
  const content = (
    <>
      <span className={cn('flex min-w-0 items-center', leading ? 'gap-1.5' : 'gap-2.5')}>
        {leading}
        <span
          className={cn(
            typographyVariant({ variant: 'p2' }),
            'truncate text-text-primary'
          )}
        >
          {label}
        </span>
      </span>
      {meta ? (
        <span
          className={cn(
            typographyVariant({ variant: 'p2' }),
            'ms-auto shrink-0 text-text-secondary'
          )}
        >
          {meta}
        </span>
      ) : null}
    </>
  );

  if (href) {
    return (
      <Link
        data-testid="routine-run-row"
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        aria-current={selected ? 'true' : undefined}
        className={cn(className, 'mx-0 w-full')}
      >
        {content}
      </Link>
    );
  }

  return (
    <button
      type="button"
      data-testid="routine-run-row"
      onClick={onClick}
      disabled={disabled}
      aria-current={selected ? 'true' : undefined}
      className={className}
    >
      {content}
    </button>
  );
}
